import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { Search, FileText, ListTodo, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";

/**
 * GlobalSearch — palette de recherche transverse ouverte par la touche "/".
 *
 * Recherche en parallèle sur :
 *   - AVI (Validation / Accusé / Information) via `/api/avi?q=…` (3 appels)
 *   - Tâches                                  via `/api/tasks?search=…`
 *   - Documents                               via `/api/documents?q=…`
 *
 * Ignore la touche "/" quand le focus est déjà dans un champ (input,
 * textarea, contenteditable) pour ne pas gêner la saisie utilisateur.
 *
 * Navigation : ↑/↓ + Enter pour ouvrir le résultat surligné.
 */
const MAX_PER_SOURCE = 5;

function isTypingContext(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.isContentEditable) return true;
  return false;
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ avi: [], tasks: [], docs: [] });
  const [active, setActive] = useState(0);
  const navigate = useNavigate();

  // Raccourci clavier "/" et Ctrl+K / ⌘K.
  useEffect(() => {
    const onKey = (e) => {
      if (open) {
        if (e.key === "Escape") { setOpen(false); return; }
      } else {
        const isCmdK = (e.key === "k" || e.key === "K") && (e.ctrlKey || e.metaKey);
        const isSlash = e.key === "/" && !isTypingContext(document.activeElement);
        if (isCmdK || isSlash) {
          e.preventDefault();
          setOpen(true);
          setQ("");
          setResults({ avi: [], tasks: [], docs: [] });
          setActive(0);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Recherche debounced.
  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults({ avi: [], tasks: [], docs: [] }); return undefined; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const [aVal, aAcc, aInf, aTasks, aDocs] = await Promise.all([
          api.get("/avi", { params: { avi_type: "validation",  q: term, per_page: MAX_PER_SOURCE } }).catch(() => ({ data: { items: [] } })),
          api.get("/avi", { params: { avi_type: "accuse",      q: term, per_page: MAX_PER_SOURCE } }).catch(() => ({ data: { items: [] } })),
          api.get("/avi", { params: { avi_type: "information", q: term, per_page: MAX_PER_SOURCE } }).catch(() => ({ data: { items: [] } })),
          api.get("/tasks", { params: { search: term, page_size: MAX_PER_SOURCE } }).catch(() => ({ data: { items: [] } })),
          api.get("/documents", { params: { q: term, page_size: MAX_PER_SOURCE } }).catch(() => ({ data: { items: [] } })),
        ]);
        const avi = [
          ...(aVal.data.items || []),
          ...(aAcc.data.items || []),
          ...(aInf.data.items || []),
        ].slice(0, MAX_PER_SOURCE + 2);
        const tasks = (aTasks.data.items || []).slice(0, MAX_PER_SOURCE);
        const docs = (aDocs.data.items || []).slice(0, MAX_PER_SOURCE);
        setResults({ avi, tasks, docs });
        setActive(0);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timer);
  }, [q, open]);

  const flat = useMemo(() => {
    const out = [];
    results.avi.forEach((it) => out.push({ kind: "avi", item: it }));
    results.tasks.forEach((it) => out.push({ kind: "task", item: it }));
    results.docs.forEach((it) => out.push({ kind: "doc", item: it }));
    return out;
  }, [results]);

  const openResult = (r) => {
    if (!r) return;
    if (r.kind === "avi")  navigate(`/avi/${r.item.id}`);
    if (r.kind === "task") navigate(`/taches/${r.item.id}`);
    if (r.kind === "doc")  navigate(`/documents/${r.item.id}`);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); openResult(flat[active]); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-24"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
      onClick={() => setOpen(false)}
      data-testid="global-search-overlay"
    >
      <div
        className="w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{ background: "#161616", border: "1px solid #2A2A2A", boxShadow: "0 20px 50px rgba(0,0,0,0.6)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="global-search-panel"
      >
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid #2A2A2A" }}>
          <Search size={16} className="text-muted-foreground" />
          <input
            type="text"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Rechercher partout — AVI, tâches, documents…"
            className="flex-1 bg-transparent text-sm placeholder:text-[#6b7280] outline-none"
            style={{ color: "#EDEDED" }}
            data-testid="global-search-input"
          />
          {loading && <Loader2 size={13} className="animate-spin text-muted-foreground" />}
          <div className="flex items-center gap-1.5">
            <kbd className="text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                 style={{ background: "#212121", border: "1px solid #383838", color: "#9CA3AF" }}>
              {typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl+K"}
            </kbd>
            <span className="text-[10px] text-muted-foreground">·</span>
            <kbd className="text-[9.5px] font-mono px-1.5 py-0.5 rounded"
                 style={{ background: "#212121", border: "1px solid #383838", color: "#9CA3AF" }}>/</kbd>
            <span className="text-[10px] text-muted-foreground ml-1">esc</span>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {q.trim().length < 2 && (
            <div className="text-center text-xs text-muted-foreground py-8" data-testid="global-search-hint">
              Tapez au moins 2 caractères pour lancer la recherche.
              <div className="mt-2 text-[10.5px]">
                Ouvrez la palette avec <kbd className="mx-1 px-1.5 py-0.5 rounded font-mono text-[9px]"
                   style={{ background: "#212121", border: "1px solid #383838" }}>Ctrl+K</kbd>
                ou <kbd className="mx-1 px-1.5 py-0.5 rounded font-mono text-[9px]"
                   style={{ background: "#212121", border: "1px solid #383838" }}>/</kbd>
                depuis n'importe quelle page.
              </div>
            </div>
          )}
          {q.trim().length >= 2 && flat.length === 0 && !loading && (
            <div className="text-center text-xs text-muted-foreground py-8" data-testid="global-search-empty">
              Aucun résultat.
            </div>
          )}
          {results.avi.length > 0 && (
            <Section title="AVI" color="#EC4899">
              {results.avi.map((it, idx) => {
                const sub = AVI_SUBTYPES[it.avi_type];
                const Icon = sub?.Icon;
                const flatIdx = idx;
                return (
                  <Row key={it.id} active={active === flatIdx} onClick={() => openResult({ kind: "avi", item: it })}
                       onMouseEnter={() => setActive(flatIdx)} testid={`global-avi-${it.id}`}>
                    {Icon && <Icon size={16} />}
                    <span className="font-mono text-[11px]" style={{ color: sub?.color || "#EC4899" }}>{it.reference}</span>
                    <span className="text-[13px] truncate flex-1">{it.subject}</span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
                      {sub?.label}
                    </span>
                  </Row>
                );
              })}
            </Section>
          )}
          {results.tasks.length > 0 && (
            <Section title="Tâches" color="#F9BC26">
              {results.tasks.map((t, idx) => {
                const flatIdx = results.avi.length + idx;
                return (
                  <Row key={t.id} active={active === flatIdx} onClick={() => openResult({ kind: "task", item: t })}
                       onMouseEnter={() => setActive(flatIdx)} testid={`global-task-${t.id}`}>
                    <ListTodo size={16} style={{ color: "#F9BC26" }} />
                    <span className="text-[13px] truncate flex-1">{t.subject}</span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-widest">{t.status || ""}</span>
                  </Row>
                );
              })}
            </Section>
          )}
          {results.docs.length > 0 && (
            <Section title="Documents" color="#2B579A">
              {results.docs.map((d, idx) => {
                const flatIdx = results.avi.length + results.tasks.length + idx;
                return (
                  <Row key={d.id} active={active === flatIdx} onClick={() => openResult({ kind: "doc", item: d })}
                       onMouseEnter={() => setActive(flatIdx)} testid={`global-doc-${d.id}`}>
                    <FileText size={16} style={{ color: "#5A9EE0" }} />
                    <span className="font-mono text-[11px]" style={{ color: "#5A9EE0" }}>{d.reference}</span>
                    <span className="text-[13px] truncate flex-1">{d.title}</span>
                  </Row>
                );
              })}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div>
      <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-semibold"
           style={{ background: "#1A1A1A", color }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ active, children, onClick, onMouseEnter, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className="w-full flex items-center gap-2 px-4 py-2 text-left transition-colors"
      style={{ background: active ? "rgba(255,255,255,0.04)" : "transparent" }}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
